import React from 'react';

import VolumeHigh from '../assets/icons/VolumeHigh.svg?react';
import VolumeMute from '../assets/icons/VolumeMute.svg?react';

import './VolumeSlider.css';

const DEFAULT_UNMUTE_LEVEL = 50;

function clamp0to100(n: number): number {
    const r = Math.round(Number(n));
    if (!Number.isFinite(r)) return 0;
    return Math.min(100, Math.max(0, r));
}

/** If audible → 0; if muted (0) → `unmuteLevel` (clamped 0–100). */
export function nextVolumeMuteToggleLevel(current: number, unmuteLevel: number = DEFAULT_UNMUTE_LEVEL): number {
    const u = clamp0to100(unmuteLevel);
    return clamp0to100(current) > 0 ? 0 : u;
}

export type VolumeSliderProps = {
    /** `id` for the range input (mute control uses `${id}-mute`). */
    id: string;
    /** Current level 0–100. */
    value: number;
    /** Called with the next level when the slider or mute button changes. */
    onChange: (next: number) => void;
    /** Level applied when leaving mute (default 50). */
    unmuteLevel?: number;
    disabled?: boolean;
    className?: string;
};

/**
 * Mute toggle (left) + 0–100 range + white `NN%` readout. Controlled: parent owns state
 * (e.g. Settings local state, or GameSettings with `patchUserSettings` on change).
 */
export const VolumeSlider: React.FC<VolumeSliderProps> = ({
    id,
    value,
    onChange,
    unmuteLevel = DEFAULT_UNMUTE_LEVEL,
    disabled = false,
    className,
}) => {
    const level = clamp0to100(value);
    const audible = level > 0;
    const muteId = `${id}-mute`;

    const handleMuteClick = (): void => {
        if (disabled) return;
        onChange(nextVolumeMuteToggleLevel(level, unmuteLevel));
    };

    const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        onChange(clamp0to100(Number(e.target.value)));
    };

    return (
        <div className={['volume_slider', className].filter(Boolean).join(' ')}>
            <button
                id={muteId}
                type="button"
                className="volume_slider_mute_btn"
                aria-label={audible ? 'Mute' : `Unmute (${clamp0to100(unmuteLevel)}%)`}
                aria-pressed={audible}
                disabled={disabled}
                onClick={handleMuteClick}
            >
                {audible ? (
                    <VolumeHigh className="volume_slider_mute_icon" aria-hidden />
                ) : (
                    <VolumeMute className="volume_slider_mute_icon" aria-hidden />
                )}
            </button>
            <input
                id={id}
                type="range"
                className="volume_slider_track"
                min={0}
                max={100}
                step={1}
                value={level}
                disabled={disabled}
                onChange={handleRangeChange}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={level}
                aria-valuetext={`${level}%`}
            />
            <span className="volume_slider_pct" aria-live="polite">
                {level}%
            </span>
        </div>
    );
};
